import React, { Fragment, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { FormattedMessage } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { GIButton, Modal } from 'app-components';

import { useMultiHousehold } from '@hooks/index';
import RadioButtons from '@lib/inputs/RadioButtons';
import { configs, PERSON_TYPE_LABELS } from '@constants/index';
import { setActiveHouseholdId } from '@actions/individualPortal/dashboard/dashboardActionCreators';
import InstructionalText from '@components/ssap/notifications/InstructionalText';
import { ConditionalRendering } from '@components/common/conditional-rendering';
import AlertMessage from '@components/individualPortal/dashboard/AlertMsgs/AlertMessage';

const ACTIVE_HOUSEHOLD_ID = 'activeHouseholdId';

const StyledContainer = styled.div`
    border: 1px solid ${({ theme }) => theme.gray8};
    border-radius: 1rem;
    margin: 1rem;
`;

const StyledMembersContainer = styled.div`
    display: flex;
    flex-direction: row;

    .radio-button-container {
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;

const StyledHouseholdMembers = styled.div`
    flex: 9;
    display: grid;
    grid-template-columns: 1fr 3fr;
    border-left: 1px solid ${({ theme }) => theme.gray8};
    padding: 1rem;
    gap: 1rem;

    .member-row {
        display: grid;
        grid-template-columns: 2fr 3fr;
    }

    .member-name {
        font-weight: ${({ theme }) => theme.fontWeightSemibold};
    }

    .household-id {
        font-weight: ${({ theme }) => theme.fontWeightSemibold};
    }

    .member-row:not(:last-of-type) {
        border-bottom: 1px solid ${({ theme }) => theme.gray8};
    }

    .member-tax-relationship {
        margin-left: 2rem;
    }
`;

const StyledModalHeader = styled.div`
    .gi-modal__btn--close {
        display: none;
    }
`;

const StyledModalBody = styled.div`
    .gi-modal__body {
        margin-bottom: 1rem;
    }
`;

function MultiHouseholdDetailsContainer(props) {
    const {
        environment: {
            devEnvironment,
            flags: { multiHouseholdEnabled },
        },
        setActiveHouseholdId,
    } = props;

    const location = useLocation();
    const { multiHouseholdDetails, setActiveHousehold } = useMultiHousehold(multiHouseholdEnabled);
    const [showMultiHouseholdDetails, setShowMultiHouseholdDetails] = useState(false);
    const [selectedHouseholdId, setSelectedHouseholdId] = useState(null);
    const [error, setError] = useState(null);
    const { routes } = configs;

    useEffect(() => {
        const activeHouseholdIdFromStorage = localStorage.getItem(ACTIVE_HOUSEHOLD_ID);
        const isInitialMP =
            location.pathname === routes.INDPORTAL &&
            location.search === '' &&
            (activeHouseholdIdFromStorage === null || activeHouseholdIdFromStorage === '');
        const isMultiHouseholdRoute = location.pathname === routes.INDPORTAL && location.search.includes('showMultiHH');
        if (
            multiHouseholdDetails.length > 0 &&
            devEnvironment !== null &&
            multiHouseholdEnabled &&
            (isInitialMP || isMultiHouseholdRoute)
        ) {
            setShowMultiHouseholdDetails(true);
        }
    }, [multiHouseholdEnabled, location.pathname, location.search, multiHouseholdDetails]);

    const onSetActiveHousehold = e => {
        if (selectedHouseholdId) {
            setError(null);
            setActiveHousehold(selectedHouseholdId)
                .then(() => {
                    setShowMultiHouseholdDetails(false);
                    setActiveHouseholdId(selectedHouseholdId);
                    localStorage.setItem(ACTIVE_HOUSEHOLD_ID, selectedHouseholdId);
                    window.location.href = `${routes.INDPORTAL}?goDash=true`;
                })
                .catch(err => {
                    console.error(
                        'In MultiHouseholdDetailsContainer: onSetActiveHousehold(): error resp from API = ',
                        err,
                    );
                    setShowMultiHouseholdDetails(false);
                });
        } else {
            setError('multiTaxHousehold.select.options.required');
        }
    };

    const renderMembers = (householdId, members) => {
        return (
            <StyledHouseholdMembers>
                <FormattedMessage id="multiTaxHousehold.householdId" />
                <span className="household-id">{householdId}</span>
                <FormattedMessage id="multiTaxHousehold.members" />
                <span className="member-container">
                    {members.map((member, index) => (
                        <div key={`${member.firstName}_${member.lastName}_${member.suffix || ''}_${index}`} className="member-row">
                            <span className="member-name">
                                {member.firstName} {member.middleName} {member.lastName} {member.suffix}
                            </span>
                            <span className="member-tax-relationship" data-testid={`member-tax-relationship-${index}`}><FormattedMessage id={(member?.taxRelationship && PERSON_TYPE_LABELS[member.taxRelationship]) || PERSON_TYPE_LABELS.NA} /></span>
                        </div>
                    ))}
                </span>
            </StyledHouseholdMembers>
        );
    };

    const renderHouseholds = () => {
        return multiHouseholdDetails.map(household => {
            return (
                <Fragment key={household.id}>
                    <StyledContainer onClick={() => setSelectedHouseholdId(household.id)}>
                        <StyledMembersContainer>
                            <div className="radio-button-container">
                                <RadioButtons
                                    label={{
                                        isLabelVisible: false,
                                    }}
                                    inputData={{
                                        id: `multiHousehold-select-${household.id}`,
                                        options: [
                                            {
                                                value: household.id,
                                                label: household.id,
                                            },
                                        ],
                                        value: selectedHouseholdId,
                                    }}
                                    legend={{
                                        legendText: 'multiTaxHousehold.header',
                                    }}
                                    inputActions={{
                                        handleFieldChange: value => {
                                            setSelectedHouseholdId(value);
                                        },
                                    }}
                                />
                            </div>
                            {renderMembers(household.id, household.members)}
                        </StyledMembersContainer>
                    </StyledContainer>
                </Fragment>
            );
        });
    };

    const HouseholdModal = ({ onSetActiveHousehold }) => {
        return (
            <Modal id="multiHouseholdModal">
                <StyledModalHeader>
                    <Modal.Header id="multiHouseholdModalHeader" titleId="multiTaxHousehold.header" />
                </StyledModalHeader>

                <StyledModalBody>
                    <Modal.Body>
                        <FormattedMessage id="multiTaxHousehold.info" />
                        <InstructionalText
                            Component={
                                <span
                                    data-instructions="multiTaxHousehold.alert.content"
                                    className="required-false margin-t-20 align-self-center"
                                />
                            }
                        />
                        <ConditionalRendering isTrue={error}>
                            <AlertMessage
                                content={<FormattedMessage id={error} />}
                                type="error"
                            />
                        </ConditionalRendering>
                        {renderHouseholds()}
                    </Modal.Body>
                </StyledModalBody>
                <Modal.Controls>
                    <GIButton
                        id="multiHouseholdModalSubmitButton"
                        onClickHandler={onSetActiveHousehold}
                        variant="primary"
                        labelId="button.submit"
                        dataTestId="multi-household-modal-submit-button"
                    />
                </Modal.Controls>
            </Modal>
        );
    };

    return (
        <Fragment>
            {showMultiHouseholdDetails && (
                <HouseholdModal onSetActiveHousehold={onSetActiveHousehold} />
            )}
        </Fragment>
    );
}

const mapStateToProps = state => ({
    environment: state.common.environment,
});

export default connect(mapStateToProps, { setActiveHouseholdId })(
    React.memo(MultiHouseholdDetailsContainer),
);
